import { useState, useEffect, useCallback } from "react";
import { Image } from "antd";
import Icons from "./Icons";
import CustomRating from "./CustomRating";
import "./ReviewItem.css";
import FollowButton from "./FollowButton";

export default function ReviewItem({ review, onProfileClick }) {
  const reviewId =
    review.id || `review_${Math.random().toString(36).substr(2, 9)}`;

  // Initialize state from the review prop, not localStorage
  const [liked, setLiked] = useState(review.is_liked || false);
  const [likeCount, setLikeCount] = useState(review.like_count || 0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const fetchLikeStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(
        `https://dev-api.curu.com.au/api/products/review/${reviewId}/status`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setLiked(data.is_liked || false);
      }
    } catch (error) {
      console.error("Error fetching like status:", error);
    }
  }, [reviewId]);

  // Check authentication status and fetch like status when component mounts
  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsAuthenticated(!!token);

    // If we have a token and the review has an ID, fetch the current like status
    if (token && review.id) {
      fetchLikeStatus();
    }
  }, [review.id, fetchLikeStatus]);

  // Update state when review prop changes
  useEffect(() => {
    setLikeCount(review.like_count || 0);
    setLiked(review.is_liked || false);
  }, [review.like_count, review.is_liked]);

  const handleLike = async () => {
    if (isLoading) return;

    // Check if user is authenticated
    if (!isAuthenticated) {
      setError("Please log in to like reviews");
      return;
    }

    setIsLoading(true);
    setError(null);

    const newLikedState = !liked;
    const oldLikeCount = likeCount;
    const newLikeCount = newLikedState ? likeCount + 1 : likeCount - 1;

    // Optimistically update UI
    setLiked(newLikedState);
    setLikeCount(newLikeCount);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `https://dev-api.curu.com.au/api/products/review/${reviewId}/like`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            like: newLikedState,
          }),
        }
      );

      if (!response.ok) {
        console.error("API Error:", response.status, response.statusText);

        // Try PUT if PATCH fails with 405 Method Not Allowed
        if (response.status === 405) {
          const putResponse = await fetch(
            `https://dev-api.curu.com.au/api/products/review/${reviewId}/like`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                like: newLikedState,
              }),
            }
          );

          if (!putResponse.ok) {
            throw new Error(
              `API Error: ${putResponse.status} ${putResponse.statusText}`
            );
          }
        } else {
          throw new Error(
            `API Error: ${response.status} ${response.statusText}`
          );
        }
      }

      try {
        const data = await response.json();
        console.log("Like updated successfully:", data);
      } catch (e) {
        console.log("Like updated (no response body)");
      }
    } catch (error) {
      console.error("Error updating like:", error);
      setError(error.message);

      // Revert UI changes if the API call failed
      setLiked(!newLikedState);
      setLikeCount(oldLikeCount);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileClick = (user) => {
    if (onProfileClick) {
      onProfileClick(user);
    }
  };

  return (
    <div className="review-item card mb-4 border rounded p-3 shadow-sm overflow-hidden d-flex flex-column justify-content-center">
      <div className="d-flex align-items-center mb-2 review-header">
        {review.author?.photo ? (
          <img
            src={review.author.photo}
            alt="Author"
            className="review-author-photo rounded-circle me-2"
            onClick={() => handleProfileClick(review.author)}
          />
        ) : (
          <span
            onClick={() => handleProfileClick(review.author)}
            style={{ cursor: "pointer" }}
          >
            <Icons.Profile className="post-user-photo rounded-circle me-2" />
          </span>
        )}

        <div className="review-author-details ms-3 flex-grow-1">
          <div className="username-follow">
            <p className="review-author-name fw-bold m-0" onClick={() => handleProfileClick(review.author)}>
              {review.author?.username || "Unknown user"}
            </p>
            <div className="follow-btn">
              <FollowButton user={review.author} />
            </div>
          </div>
          <p className="review-author-info text-muted mb-0 small">
            {review.author?.country || "Unknown"} • {review.author?.age || "?"}{" "}
            years • {review.author?.skin || "Unknown skin type"}
          </p>
        </div>

        <div className="reviewer-rating mt-1">
          <span className="rating-stars">
            <CustomRating rate={review.rate} size={18} />
          </span>
          <span className="text-muted">
            {" "}
            ({review.author?.review_count || 0})
          </span>
        </div>
      </div>

      <div className="review-body d-flex justify-content-between align-items-center mt-3">
        <div className="review-text-and-photos">
          <p className="review-text">{review.text || "No review text"}</p>
          {review.photos && review.photos.length > 0 && (
            <div className="d-flex mb-3 gap-2">
              {review.photos.map((photo) => (
                <Image
                  key={photo.id || Math.random()}
                  src={photo.photo}
                  alt="photo"
                  width={100}
                  height={100}
                  className="review-photo rounded"
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="like-comment-buttons d-flex justify-content-end align-items-center me-2 mt-2">
        <button
          className={`like-button btn p-0 bg-transparent border-0 d-flex align-items-center ${
            isLoading ? "disabled" : ""
          }`}
          onClick={handleLike}
          disabled={isLoading}
          type="button"
        >
          {isLoading ? (
            <span
              className="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            ></span>
          ) : (
            <img
              src={
                liked
                  ? "/images/icons/heart-filled.svg"
                  : "/images/icons/heart.svg"
              }
              alt="like"
              className="me-1"
            />
          )}
          <span>{likeCount}</span>
        </button>

        {error && <span className="text-danger ms-2 small">{error}</span>}
      </div>
    </div>
  );
}
